import { css } from '@emotion/react';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';
import { ScheduleRequestListItem } from '../../../../../../../types';
import { safeParseJson } from '../../../../../../../utils';

interface ScheduleRequestDetailsProps {
  listItem: ScheduleRequestListItem;
}

export const PreferenceBasedScheduleRequestDetails = ({ listItem }: ScheduleRequestDetailsProps) => {
  const { t } = useTranslation('schedule-requests');

  const parsedJson = safeParseJson<any>(listItem.bookingDetails, null);
  const bookingCaptureSource = parsedJson?.sourceDetails?.source;
  const hasValidBookingCaptureSource = bookingCaptureSource === 'VOICE_AI' || bookingCaptureSource === 'SMS_AI';
  const phoneNumber = listItem.schedulee?.phoneNumber;
  const email = listItem.schedulee?.email;
  const isPatientContacted = listItem.reviewedStatus !== 'PENDING';
  const lastContactedOn = formatDate(listItem.updatedAt, 'MMM D, YYYY, h:mm A');

  return (
    <div css={containerStyles}>
      <div>
        <Text size='small' color='subdued'>
          {t('Contact information')}
        </Text>
        <span css={contactItemStyles}>
          <Icon name='phone-small' />
          <Text size='small' color='light'>
            {formatPhoneNumber(phoneNumber)}
          </Text>
        </span>
        {email && (
          <span css={contactItemStyles}>
            <Icon name='email-small' />
            <Text size='small' color='light'>
              {email}
            </Text>
          </span>
        )}
      </div>

      <div>
        <Text size='small' color='subdued'>
          {t('Availability Preferences')}
        </Text>
        <Text size='small' color='light'>
          {parsedJson?.schedulingPreferences?.requestedSlots?.[0]?.rawText}
        </Text>
      </div>
      <div>
        <Text size='small' color='subdued'>
          {t('Reason for Visit')}
        </Text>
        <Text size='small' color='light'>
          {parsedJson?.visitDetails?.reasonForVisit}
        </Text>
      </div>

      {hasValidBookingCaptureSource && (
        <div css={viewThreadButtonStyles}>
          {bookingCaptureSource === 'VOICE_AI' && (
            <Button variant='tertiary' iconName='phone'>
              {t('Review Call Log')}
            </Button>
          )}
          {bookingCaptureSource === 'SMS_AI' && (
            <Button variant='tertiary' iconName='message'>
              {t('View Conversation')}
            </Button>
          )}
        </div>
      )}

      <div css={footerContainerStyles}>
        <div>
          <Text size='small' color='subdued'>
            {t('Contacted on {{lastContactedOn}}', { lastContactedOn })}
          </Text>
        </div>
        <div css={footerButtonStyles}>
          <div css={contactButtonStyles}>
            <Button iconName='message' variant='secondary'>
              {t('Message')}
            </Button>
            <Button iconName='phone' variant='secondary'>
              {t('Call')}
            </Button>
          </div>
          {isPatientContacted ? (
            <Button variant='tertiary'>{t('Done')}</Button>
          ) : (
            <Button variant='tertiary'>{t('Dismiss')}</Button>
          )}
        </div>
      </div>
    </div>
  );
};

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--fabric-space-gap-md)',
  padding: 'var(--fabric-space-padding-sm)',
  borderRadius: 'var(--fabric-radius-sm)',
  boxShadow: theme.shadows.light,
  background: theme.colors.white,
});

const contactItemStyles = css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-xxs);
  color: var(--fabric-color-neutral-70);
`;

const viewThreadButtonStyles = css`
  & > button {
    padding: var(--fabric-space-padding-sm) var(--fabric-space-padding-xxs);
  }
`;

const footerContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--fabric-space-gap-xxs)',
});

const footerButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--fabric-space-gap-xl);
`;

const contactButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--fabric-space-gap-sm);
  flex: 1;

  & > button {
    color: var(--fabric-color-neutral-70);
    flex: 1;
  }
`;
